import { Control, IControl } from '@Common/Controls/Controls';
import { GridLayout, IGridCellDescription, IGridColumnDescription, IGridLayoutParameters, IGridRowDescription } from '@Common/Controls/GridLayout';
import { BaseControl } from '@Common/Controls/BaseControl';
import { CheckBox } from '@Common/Controls/CheckBox';
import { Label } from '@Common/Controls/Label';
import { enumerable } from '@Common/Core/CoreDecorator';
import { px } from './Units';

export type LayoutBuilderSetting = { isMainGrid: boolean; hasSpinner: boolean };
export type CellAppearance = { style?: Partial<CSSStyleDeclaration>; flex?: boolean };

export interface IControlCellParameter {
    filled?: boolean;
    style?: Partial<CSSStyleDeclaration>;
    adjusting?: boolean;
    className?: string;
    colSpan?: number;
}

type AllControl = Control | JQuery | string;

export class LayoutBuilder {
    private readonly isMainGrid: boolean = false;
    private readonly hasSpinner: boolean = false;
    private readonly rows: IGridRowDescription[] = [];
    private readonly cells: IGridCellDescription[] = [];
    private hasFilled = false;
    private readonly classes: string[] = [];

    constructor();

    constructor(isMainGrid: boolean);
    constructor(param: { isMainGrid: boolean; hasSpinner: boolean });
    constructor(param: boolean | LayoutBuilderSetting = { hasSpinner: false, isMainGrid: false }) {
        if (_.isBoolean(param)) {
            this.isMainGrid = param;
        } else {
            _.assign(this, param);
        }
    }

    @enumerable
    public get rowsCount(): number {
        return this.rows.length;
    }

    private static buildString(str: string): BaseControl {
        return new Label({ caption: str, css: { display: 'inline-block' } });
    }

    private static buildControl(placeHolder: string, controls: BaseControl[]): BaseControl {
        const ctrlIndex = parseInt(placeHolder.substring(1, placeHolder.length - 1));
        return controls[ctrlIndex];
    }

    public addClassName(className: string): void {
        this.classes.push(className);
    }

    public formatControls(format: string, controls: BaseControl[], checkable = false, className: string = null): CheckBox {
        let check: CheckBox = null;
        const items = this.buildControlByStringFormat(format, ...controls);
        if (checkable && items) {
            for (const obj of items) {
                obj.enabled(false);
            }
            let checkCaption = '';
            if (items.length > 0 && items[0] instanceof Label) {
                const label = items.shift() as Label;
                checkCaption = label.text();
            }
            check = new CheckBox({ inlineBlockElement: true, caption: checkCaption });
            items.insert(0, check);
            check.onChange(checked => {
                for (const ctrl of items) {
                    if (ctrl !== check) {
                        ctrl.enabled(checked);
                    }
                }
            });
        }
        this.controlRow(items, null, className);
        return check;
    }

    public addSwitchOff(title: string, slave: BaseControl, className: string = null): CheckBox {
        const switchOff = new CheckBox({ caption: title });
        switchOff.onChange(checked => slave.enabled(checked));
        slave.enabled(false);

        this.cells.push({
            rowIndex: this.rows.length,
            columnIndex: 0,
            className,
            table: {
                className: 'switchOffControl ',
                rows: [{ style: { height: 'auto' } }, { style: { height: 'auto' } }],
                columns: [{}],
                cells: [
                    { columnIndex: 0, rowIndex: 0, content: switchOff, className: 'switchOff' },
                    { columnIndex: 0, rowIndex: 1, content: slave, className: 'slaveControl' }
                ]
            }
        });
        this.rows.push({ style: { height: 'auto' } });
        return switchOff;
    }

    public addOneControl(ctrl: AllControl, filled = false, cellStyle: Partial<CSSStyleDeclaration> = null, className: string = null): void {
        this.cells.push({rowIndex: this.rows.length, columnIndex: 0, content: ctrl, style: cellStyle, className});
        this.rows.push({style: {height: filled ? '*' : 'auto'}});
        this.hasFilled = this.hasFilled || filled;
    }

    public addOneControlWithParameter(ctrl: IControl | any, parameter: IControlCellParameter): void {
        const filled = parameter.filled === true;
        this.cells.push({
            rowIndex: this.rows.length,
            columnIndex: 0,
            content: ctrl,
            style: parameter.style,
            className: parameter.className,
            adjusting: parameter.adjusting,
            colSpan: parameter.colSpan
        });
        this.rows.push({ style: { height: filled ? '*' : 'auto' } });
        this.hasFilled = this.hasFilled || filled;
    }

    public addTable(table: IGridLayoutParameters, {style, flex}: CellAppearance = {flex: false}): void {
        this.cells.push({rowIndex: this.rows.length, columnIndex: 0, table, style});
        this.rows.push({style: {height: flex ? '*' : 'auto'}});
        this.hasFilled = this.hasFilled || flex;
    }

    public addControlWithParameter(title: string, ctrl: AllControl, parameter: IControlCellParameter): void {
        this.addTitle(title);
        this.addOneControlWithParameter(ctrl, parameter);
    }

    public addControl(title: string, ctrl: AllControl, filled = false, cellStyle: any = null, className: string = null): void {
        this.addTitle(title);
        this.addOneControl(ctrl, filled, cellStyle, className);
    }

    public addControls(...ctrls: { title: string; control: IControl; size?: string, minSize?: string }[]): void {
        const cells: IGridCellDescription[] = [];
        const columns: IGridColumnDescription[] = [];
        const filled = ctrls.some(c => c.size && c.size.indexOf('*') >= 0);
        for (const ctrl of ctrls) {
            cells.push({content: ctrl.title, rowIndex: 0, columnIndex: columns.length});
            cells.push({content: ctrl.control, rowIndex: 1, columnIndex: columns.length});
            columns.push({style: {width: ctrl.size ? ctrl.size : 'auto', minWidth: ctrl.minSize}});
        }

        if (!filled) {
            columns.push({ style: { width: '*' } });
        }

        this.cells.push({
            columnIndex: 0,
            rowIndex: this.rows.length,
            table: {
                cells,
                columns,
                rows: [{ style: { height: 'auto' } }, { style: { height: 'auto' } }]
            }
        });
        this.rows.push({ style: { height: 'auto' } });
    }

    public addControlsWithFill(...ctrls: { title: string; control: IControl; size?: string }[]): void {
        this.addControls(...ctrls);
        const controlsRow = _.last(_.last(this.cells).table.rows);
        controlsRow.style.height = '*';
        this.rows.removeAt(-1);
    }

    public addTitle(title: string, className: string = null): void {
        this.cells.push({rowIndex: this.rows.length, columnIndex: 0, content: title, className});
        this.rows.push({style: {height: 'auto'}});
    }

    public addEmptyRow(): void {
        this.addTitle(' ');
    }

    public addGroupControl(title: string, ctrls: AllControl[], sizes?: string[]): void {
        this.addTitle(title);
        this.controlRow(ctrls, sizes);
    }

    public controlRow(ctrls: AllControl[], sizes?: string[], classNameOrCss?: string | Partial<CSSStyleDeclaration>): void {
        const cells: IGridCellDescription[] = [];
        const columns: IGridColumnDescription[] = [];
        if (!sizes) {
            sizes = ctrls.map(c => 'auto');
        }
        ctrls.forEach((ctrl, i) => {
            const style = i > 0 && !(ctrl instanceof Label) ? { paddingLeft: '5px' } : {};
            cells.push({ rowIndex: 0, columnIndex: i, content: ctrl, style, adjusting: false });

            columns.push({ style: { width: sizes[i] } });
        });

        if (!_.includes(sizes, '*')) {
            cells.push({ rowIndex: 0, columnIndex: columns.length, style: { width: '100%' } });
            columns.push({ style: { width: '*' } });
        }

        const css = _.isString(classNameOrCss) ? {} : classNameOrCss ?? {};
        const className = _.isString(classNameOrCss) ? classNameOrCss : '';

        this.cells.push({
            rowIndex: this.rows.length,
            columnIndex: 0,
            className,
            style: css,
            table: {
                columns,
                rows: [{ style: { height: 'auto' } }],
                cells
            }
        });

        this.rows.push({ style: { height: 'auto' } });
    }

    public createLayout(): GridLayout {
        return new GridLayout(this.getLayout());
    }

    public getLayout(
        { filling, width }: { filling?: boolean; width?: string | number } = {
            filling: true,
            width: '*'
        }
    ): IGridLayoutParameters {
        if (!this.hasFilled && filling) {
            this.rows.push({ style: { height: '*' } });
        }
        if (this.isMainGrid) {
            this.classes.insert(0, 'mainGrid');
        }
        return {
            hasSpinner: this.hasSpinner,
            className: this.classes.join(' '),
            cells: this.cells,
            rows: this.rows,
            columns: [{ style: { width: px(width) } }]
        };
    }

    private buildControlByStringFormat(fmt: string, ...controls: BaseControl[]): BaseControl[] {
        const regex = /(\{\d+\})/;
        const items = fmt.split(regex).filter(s => !!s);
        return items.map(s => (regex.test(s) ? LayoutBuilder.buildControl(s, controls) : LayoutBuilder.buildString(s)));
    }
}
